const Branch = require('@/lib/Branches/Branch');
const EventNames = require('@/lib/Events/EventNames');
const LocalStorageKeys = require('@/lib/Storage/LocalStorageKeys');
const Sentry = require('@sentry/browser');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const User = require('@/lib/Users/User');
const { container } = require('@/container');
const { waitForPromiseWithTimeout } = require('@/functions/functions');

/**
 * @constructor
 *
 * @param $injector
 * @param $rootScope
 * @param {Hydrator} hydrator
 * @param {LocalStorageDataStore} localStorageDataStore
 * @param {LogoService} logoService
 * @param {SyncStateManager} syncStateManager
 * @param {SyncRunner} syncRunner
 * @param userResource
 * @param {UserSessionManager} userSessionManager
 */
const UserService = function (
    $injector,
    $rootScope,
    hydrator,
    localStorageDataStore,
    logoService,
    syncStateManager,
    syncRunner,
    userResource,
    userSessionManager,
) {
    this.$injector = $injector;
    this.$rootScope = $rootScope;
    this.hydrator = hydrator;
    this.localStorageDataStore = localStorageDataStore;
    this.logoService = logoService;
    this.syncStateManager = syncStateManager;
    this.syncRunner = syncRunner;
    this.userResource = userResource;
    this.userSessionManager = userSessionManager;

    /**
     * The current "working branch". Probably where the till is physically located.
     * Orders will be created from this branch.
     *
     * @type {?Branch}
     */
    this.currentBranch = null;

    /**
     * The branch that we are currently displaying items for.
     * When this changes we need to sync all the item types, but not other things.
     * This will usually be the same as currentBranch, unless cross branch selling is enabled.
     *
     * @type {?Branch}
     */
    this.currentItemBranch = null;

    /**
     * @type {?User}
     */
    this.currentUser = null;

    /**
     * @type {?UserSession}
     */
    this.currentUserSession = null;

    /**
     * This is either the API key for the current user, or the last non-empty API key we knew about.
     * Once set it should never be set back to null/empty as we often need an API key even while the user is not
     * logged in, and it's better to have an old key than none.
     * TODO: Replacing user API keys with a device API key would be a good idea.
     *
     * @type {?string}
     */
    this.apiKey = null;

    this.localStorageKey = 'user';

    /**
     * This will be true if a logout is in progress but not finished yet. This is used to prevent multiple
     * logout calls happening at the same time and breaking.
     *
     * @type {boolean}
     */
    this.logoutPending = false;

    /**
     * Watch localStorage to check if the user logs in or out in a different tab (previously in NavigationCtrl).
     * This works because the 'storage' event only fires when localStorage changes in *another* tab. If we change it
     * in the same tab the event does not fire.
     *
     * @param {StorageEvent} event
     */
    const onStorageChange = (event) => {
        if (event && event.key && event.key === this.localStorageKey) {
            let oldValue = event.oldValue ? JSON.parse(event.oldValue) : {};
            let newValue = event.newValue ? JSON.parse(event.newValue) : {};

            if (!oldValue.currentUser || !newValue.currentUser || oldValue.currentUser.ID !== newValue.currentUser.ID) {
                // User appears to have changed user in another tab, so logout here.
                console.log('Page reloading due to changed user in localstorage.');
                window.location.reload();
            }
        }
    };
    if (typeof window !== 'undefined') {
        window.addEventListener('storage', onStorageChange, false);
    }
};

UserService.prototype = {
    /**
     * Write the current user and branch to storage.
     */
    saveToCache() {
        const cached = {
            apiKey: this.apiKey,
            currentBranch: this.currentBranch,
            currentItemBranch: this.currentItemBranch,
            currentUser: this.currentUser,
        };

        this.localStorageDataStore.persist(this.localStorageKey, cached);
    },

    /**
     * Load the current user and branch from storage.
     */
    async loadFromCache() {
        const cachedData = this.localStorageDataStore.find(this.localStorageKey);

        if (cachedData && cachedData.apiKey) {
            // It's possible for there to be a cached apiKey but no cached currentUser,
            // so set the apiKey first if one exists regardless of there being a user.
            this.setApiKey(cachedData.apiKey);
        }

        if (cachedData && cachedData.currentUser) {
            const currentUser = new User();
            this.hydrator.hydrate(cachedData.currentUser, currentUser);

            await this.setCurrentUser(currentUser, false);

            if (cachedData.currentBranch) {
                this.setOrderBranch(this.hydrator.hydrate(cachedData.currentBranch, new Branch()));
            }

            if (cachedData.currentItemBranch) {
                this.setItemBranch(this.hydrator.hydrate(cachedData.currentItemBranch, new Branch()));
            }

            if (this.currentBranch && !this.currentItemBranch) {
                // We have an order branch but no item branch.
                // Use the order branch as the item branch.
                this.setItemBranch(this.currentBranch);
            }
        } else {
            await this.setCurrentUser(null);
        }

        this.onUserChange(true);

        console.log('UserService.loadFromCache done');
    },

    /**
     * Fetch an updated copy of the current user (including the company and branches) from the API.
     *
     * @return {Promise<User>}
     */
    refreshCurrentUser() {
        if (!this.apiKey) {
            return Promise.reject(new Error('There is no current API key.'));
        }

        // Preserve the previous entraIDToken from the user so that it doesn't get lost during the sync.
        // We only have this locally, it doesn't come from the API.
        const entraIDToken = this.currentUser.entraIDToken;

        return new Promise((resolve, reject) => {
            this.userResource.check(
                {
                    apiKey: this.apiKey,
                },
                async (response) => {
                    if (response.user) {
                        // Extract any logos (base64 encoded images) from the response and store them
                        // in the ImageRepository instead of in local storage.
                        await this.logoService.extractLogosFromUserData(response.user);

                        // Set the entraIDToken back in the response data so it gets hydrated into the user model.
                        response.user.entraIDToken = entraIDToken;

                        let user = this.hydrator.hydrate(response.user, new User());
                        user.apiKey = this.apiKey;
                        await this.changeUser(user);
                        resolve(user);
                    } else {
                        reject(new Error('The user was missing from the user update API response.'));
                    }
                },
                () => {
                    reject(new Error('The API request to update the current user failed.'));
                },
            );
        });
    },

    notifyLoginCallbacks(fromCache) {
        this.$rootScope.$broadcast(EventNames.USER_LOGGED_IN, {
            user: this.currentUser,
            fromCache,
        });
    },

    /**
     * @param {Branch} previousBranch
     * @param {Branch} newBranch
     */
    notifyOrderBranchChangeCallbacks(previousBranch, newBranch) {
        this.$rootScope.$broadcast(EventNames.ORDER_BRANCH_CHANGED, { previousBranch, newBranch });
    },

    /**
     * @param {Branch} previousBranch
     * @param {Branch} newBranch
     */
    notifyItemBranchChangeCallbacks(previousBranch, newBranch) {
        this.$rootScope.$broadcast(EventNames.ITEM_BRANCH_CHANGED, { previousBranch, newBranch });
    },

    async initialize() {
        console.log('UserService.initialize');
        await this.loadFromCache();
    },

    /**
     * This method is called when a user logs in, logs out, or is changed via the switch user modal.
     * It is NOT called when loading an existing user from cache on startup.
     *
     * @param {User} user
     */
    async changeUser(user) {
        await this.setCurrentUser(user);

        this.saveToCache();
        this.onUserChange(false);

        if (!this.currentUser) {
            // We switched to no user (logged out).
            // Clear the selected branches.
            this.setOrderBranch(null);
            this.setItemBranch(null);
        }
    },

    /**
     * @param {Device} currentDevice
     */
    selectAppropriateBranches(currentDevice) {
        // Check if the new user has access to the existing order branch,
        // or switch it to an appropriate branch if not.
        this.setOrderBranch(
            this.determineAppropriateBranchForNewUser(this.currentUser, this.currentBranch, currentDevice)
        );

        // Check if the new user has access to the existing item branch,
        // or switch it to an appropriate branch if not.
        this.setItemBranch(
            this.determineAppropriateBranchForNewUser(this.currentUser, this.currentItemBranch, currentDevice)
        );
    },

    /**
     * @private
     * This is used when the user changes. At that point there might be an order branch
     * and an item branch already selected. The new user may or may not have access to
     * one or both of those branches.
     * Determine which branch to use based on if their is an existing branch and the user
     * has access to that existing branch.
     *
     * The same logic happens for both the order branch and the item branch.
     *
     * @param {User} user
     * @param {Branch|null} currentlySelectedBranch
     * @param {Device|null} currentDevice
     *
     * @return {Branch|null}
     */
    determineAppropriateBranchForNewUser(user, currentlySelectedBranch, currentDevice) {
        if (currentlySelectedBranch?.ID) {
            // A branch is already selected.
            // Check if the new user also has access to the already selected branch.
            const branchFromUser = user.getBranchByID(currentlySelectedBranch.ID);
            if (branchFromUser) {
                // New user has access to the current branch. We can keep using this one.
                // Return the branch from the new user in case the branch data is newer.
                return branchFromUser;
            }
        }

        // Either there is no current branch, or the new user does not have access to it.

        // Try to use the default branch from the device if there is one and the user has access.
        if (currentDevice?.defaultBranch) {
            const branchFromUser = user.getBranchByID(currentDevice.defaultBranch.ID);
            if (branchFromUser) {
                // The user has access to the device's default branch. Use that one.
                return branchFromUser;
            }
        }

        // Just use the first available branch from the user.
        // In rare cases where the user has access to no branches this might also be null.
        return this.currentUser.getFirstActiveBranch();
    },

    /**
     * Set the currentUser property and API key only.
     *
     * @param {?User} user
     * @param {boolean} [setLastSync]
     */
    async setCurrentUser(user, setLastSync = true) {
        this.currentUser = user;
        this.setApiKey(user ? user.apiKey : null);

        if (user) {
            this.currentUserSession = await this.userSessionManager.getOrCreateSession(user);
        } else {
            this.currentUserSession = null;
        }

        if (user && setLastSync) {
            // Store a "last synced" date for the current user.
            // This is used to make it so the current user is only refreshed when you click the Sync button and not
            // every time you open ProPoint.
            this.syncStateManager.saveLastSync('currentUser', null);
        } else if (!user) {
            this.syncStateManager.remove('currentUser');
        }
    },

    /**
     * Lock the current session so the user has to unlock the screen before continuing.
     *
     * @return {Promise<void>}
     */
    async lockCurrentUserSession() {
        if (this.currentUserSession) {
            this.currentUserSession.isLocked = true;
            await this.persistCurrentUserSession();
        }
    },

    /**
     * Save the current user session to IndexedDB. This should be called after any changes to the currentUserSession.
     *
     * @returns {Promise}
     */
    async persistCurrentUserSession() {
        if (this.currentUserSession) {
            await this.userSessionManager.persist(this.currentUserSession);
        }
    },

    /**
     * @private
     * @param fromCache
     */
    onUserChange(fromCache) {
        if (this.currentUser) {
            this.notifyLoginCallbacks(fromCache);

            // Entra ID Login:
            // If a user has logged in with Entra ID, when they logout we use the Microsoft SDK to show a pop up that
            // allows them to logout of their Microsoft account.
            // This shows a browser pop up to do that.
            // Before doing that we have to call the init method on the Entra ID service.
            // In Firefox there is a problem because the popup is not opened directly in response to user interaction,
            // because it waited for the init call, so the popup gets blocked.
            // To solve this we will make sure Entra ID is initialized now so we don't have to wait it to init
            // when logging out.
            if (this.currentUser.entraIDToken) {
                container.entraIDAuthService.init()
                    .then(() => console.debug('Entra ID initialized.'))
                    .catch((e) => console.error('Entra ID init failed.', e));
            }
        }
    },

    /**
     * Remembers the given username for when we to pre-fill the username on the login screen.
     * It saves the whole cache afterwards.
     *
     * @param {string|null} username
     */
    setAutofillLoginUsername(username) {
        if (username) {
            this.localStorageDataStore.persist(LocalStorageKeys.AUTO_FILL_LOGIN_USERNAME, username);
        } else {
            this.localStorageDataStore.remove(LocalStorageKeys.AUTO_FILL_LOGIN_USERNAME);
        }
    },

    /**
     * @return {?string}
     */
    pullAutofillLoginUsername() {
        return this.localStorageDataStore.pull(LocalStorageKeys.AUTO_FILL_LOGIN_USERNAME);
    },

    /**
     * Set an error message to be displayed on the login page next time it is shown.
     *
     * @param {string|null} langKey
     */
    setLoginErrorMessageKey(langKey) {
        if (langKey) {
            this.localStorageDataStore.persist(LocalStorageKeys.LOGIN_ERROR_MESSAGE_KEY, langKey);
        } else {
            this.localStorageDataStore.remove(LocalStorageKeys.LOGIN_ERROR_MESSAGE_KEY);
        }
    },

    /**
     * Get an error message to be displayed on the login page next time it is shown.
     * Removes it from storage after retrieval.
     *
     * @returns {string|null}
     */
    pullLoginErrorMessageKey() {
        return this.localStorageDataStore.pull(LocalStorageKeys.LOGIN_ERROR_MESSAGE_KEY);
    },

    /**
     * @param {string} [processingMessage]
     */
    logout(processingMessage) {
        if (this.logoutPending) {
            console.debug('Logout already in progress.');
            return;
        }
        this.logoutPending = true;
        console.debug('Logging out...');

        // Inform Sentry that they've logged out (by sending empty)
        Sentry.setUser(null);

        // Show logging out message.
        this.$rootScope.rootProcessingMessage = processingMessage || 'LOGIN.LOGGING_OUT';

        /**
         * We need to use the $injector to get this because having it in the constructor causes a circular dependency.
         *
         * @type {VerifoneAuthHandler}
         */
        const verifoneAuthHandler = this.$injector.get('verifoneAuthHandler');

        Promise
            .allSettled(
                [
                    this.entraIDLogout(),

                    // Unlock all sessions as we are going to the login screen anyway.
                    this.userSessionManager.unlockAll(),

                    // Send (or queue if offline) a user logout trading session event.
                    this.createUserLogoutTradingSessionEvent(),

                    // If we are using Verifone logout of the terminal. (verifoneAuthHandler checks if Verifone is
                    // in use.). Give it a max of 5 seconds to do its thing before we continue logging out anyway. If
                    // it finishes sooner than 5 seconds this won't wait the full 5 seconds.
                    waitForPromiseWithTimeout(verifoneAuthHandler.logout(), 5000),
                ],
            )
            .then(async () => {
                await this.changeUser(null);
                window.location.reload();
            });
    },

    async entraIDLogout() {
        if (!this.currentUser.entraIDToken) {
            return;
        }
        // init() will return a promise which immediately resolves if it is already initialized.
        // But we don't want to risk Firefox blocking popups by awaiting anything before the popup is shown,
        // so only call init if we know we need to.
        if (!container.entraIDAuthService.isInitialized()) {
            await container.entraIDAuthService.init();
        }
        await container.entraIDAuthService.logout();
    },

    /**
     * @return {Promise<OnlineTask>}
     */
    async createUserLogoutTradingSessionEvent() {
        /**
         * We need to use the $injector to get this because having it in the constructor causes a circular dependency.
         *
         * @type {TradingSessionManager}
         */
        const tradingSessionManager = this.$injector.get('tradingSessionManager');

        return tradingSessionManager.saveEvent(
            (new TradingSessionEvent(TradingSessionEventType.USER_LOGOUT))
                .setOperatorID(this.getUserID()),
            true
        );
    },

    /**
     * Public method to set the current order branch.
     *
     * @param {Branch} branch
     */
    changeOrderBranch(branch) {
        const changed = this.setOrderBranch(branch);
        if (changed) {
            // Reload the page so the correct items are synced.
            this.reloadAfterBranchChange();
        }
    },

    /**
     * @private
     * Private method to set the order branch to the desired value.
     * The new branch might be null to select no branch.
     *
     * @param {Branch|null} newOrderBranch
     */
    setOrderBranch(newOrderBranch) {
        console.debug('Setting order branch to', newOrderBranch?.name);

        const previousOrderBranch = this.currentBranch;

        this.currentBranch = newOrderBranch;
        this.saveToCache();

        if (
            (!previousOrderBranch && !newOrderBranch)
            || (previousOrderBranch && newOrderBranch && previousOrderBranch.ID === newOrderBranch.ID)
        ) {
            // This is the same branch. Don't do anything else.
            console.debug('This order branch was already selected', newOrderBranch ? newOrderBranch.ID : null);
            return false;
        }

        this.notifyOrderBranchChangeCallbacks(previousOrderBranch, newOrderBranch);

        return true;
    },

    /**
     * @return {boolean}
     */
    canChangeItemBranch() {
        return this.currentUser
            && this.currentUser.company.settings.enableCrossBranchSelling
            && this.currentUser.branches.length > 1;
    },

    /**
     * Public method to set the current items branch.
     *
     * @param {Branch} branch
     * @return {Promise}
     */
    async changeItemBranch(branch) {
        const changed = this.setItemBranch(branch);
        if (!changed) {
            throw new Error('The item branch was not changed.');
        }

        // Sync items again to ensure we have the data for the new branch.
        // This will not clear the items from the previous branches already synced.
        return this.syncRunner.syncItems(this);
    },

    /**
     * @private
     * Private method to set the item branch to the desired value.
     * The new branch might be null to select no branch.
     *
     * @param {Branch} newItemBranch
     */
    setItemBranch(newItemBranch) {
        console.debug('Setting item branch to', newItemBranch?.name);

        const previousItemBranch = this.currentItemBranch;

        this.currentItemBranch = newItemBranch;
        this.saveToCache();

        if (
            (!previousItemBranch && !newItemBranch)
            || (previousItemBranch && newItemBranch && previousItemBranch.ID === newItemBranch.ID)
        ) {
            // This is the same branch. Don't do anything else.
            console.debug('This item branch was already selected', newItemBranch ? newItemBranch.ID : null);
            return false;
        }

        this.notifyItemBranchChangeCallbacks(previousItemBranch, newItemBranch);

        return true;
    },

    /**
     * Public method to change both the order branch and item branch at the same time.
     * If you want to change both you should use this method, because there will be
     * a reload after a branch change.
     *
     * @param {Branch|null} orderBranch
     * @param {Branch|null} itemBranch
     */
    changeBranches(orderBranch, itemBranch) {
        const orderBranchChanged = this.setOrderBranch(orderBranch);
        const itemBranchChanged = this.setItemBranch(itemBranch);

        if (orderBranchChanged || itemBranchChanged) {
            // This always does a full page refresh.
            // You can change the item branch only and prevent a full page refresh by calling changeItemBranch.
            this.reloadAfterBranchChange();
        }
    },

    reloadAfterBranchChange() {
        if (typeof window !== 'undefined') {
            setTimeout(() => {
                window.location.reload();
            }, 50);
        }
    },

    /**
     * @param {number} branchID
     *
     * @return {?Branch}
     */
    getBranchByID(branchID) {
        return this.currentUser ? this.currentUser.getBranchByID(branchID) : null;
    },

    /**
     * @param {OrderLine[]} lines
     */
    attachBranchesToOrderLines(lines) {
        lines.forEach((line) => {
            line.branch = this.getBranchByID(line.branchID);
        });
    },

    /**
     * @return {boolean}
     */
    isLoggedIn() {
        return this.currentUser !== null;
    },

    /**
     * @return {?string}
     */
    getApiKey() {
        if (this.apiKey) {
            return this.apiKey;
        }

        if (this.currentUser && this.currentUser.apiKey) {
            return this.currentUser.apiKey;
        }

        return null;
    },

    /**
     * Set the API key, as long as it is a non-empty value.
     *
     * @param {string} apiKey
     */
    setApiKey(apiKey) {
        if (apiKey) {
            this.apiKey = apiKey;
        }
    },

    /**
     * @return {?number}
     */
    getOrderBranchID() {
        return this.currentBranch ? this.currentBranch.ID : null;
    },

    /**
     * @return {?number}
     */
    getItemBranchID() {
        return this.currentItemBranch ? this.currentItemBranch.ID : null;
    },

    /**
     * @return {?number}
     */
    getUserID() {
        return this.currentUser ? this.currentUser.ID : null;
    },

    /**
     * @return {?string}
     */
    getUsername() {
        return this.currentUser ? this.currentUser.username : null;
    },

    /**
     * @return {?Company}
     */
    getCompany() {
        return this.currentUser ? this.currentUser.company : null;
    },

    /**
     * @return {?number}
     */
    getUsergroupID() {
        return this.currentUser ? this.currentUser.usergroupID : null;
    },

    // Getters for backward compatibility...

    /**
     * @return {?number}
     */
    get ID() {
        return this.currentUser ? this.currentUser.ID : null;
    },

    /**
     * @type {Branch[]}
     */
    get branches() {
        return this.currentUser ? this.currentUser.branches : [];
    },

    /**
     * @return {?Company}
     */
    get company() {
        return this.currentUser ? this.currentUser.company : null;
    },

    /**
     * @return {?object}
     */
    get paymentTerminalConfig() {
        return this.currentUser ? this.currentUser.paymentTerminalConfig : null;
    },

    /**
     * @return {?number}
     */
    get usergroupID() {
        return this.currentUser ? this.currentUser.usergroupID : null;
    },

    /**
     * @return {?string}
     */
    get username() {
        return this.currentUser ? this.currentUser.username : null;
    },
};

module.exports = UserService;
